"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldLocation = exports.ModelMutatorLocation = void 0;
exports.extractLocationRaw = extractLocationRaw;
const core_api_1 = require("@ikegps/core-api");
const object_1 = require("../../../util/object");
const collection_field_1 = require("../collection.field");
const DEFAULT_LOCATION_DATA = {
    latitude: 0,
    longitude: 0,
    altitude: 0,
    accuracy: 0,
};
const LocationRange = {
    latitude: { min: -90, max: 90 },
    longitude: { min: -180, max: 180 },
};
function extractLocationRaw(raw) {
    // To keep type safety around the raw location
    // data we need to assign only the keys we care about and are not null
    return {
        antennaHeight: raw.antennaHeight,
        fix: raw.fix,
        module: raw.module,
        capturedAt: raw.capturedAt,
        undulation: raw.undulation,
    };
}
class ModelMutatorLocation extends collection_field_1.ModelMutator {
    manual(newValue) {
        if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
            this.clear();
        }
        else {
            const value = { latitude: newValue.latitude, longitude: newValue.longitude, altitude: newValue.altitude };
            this.state.setFieldValue(this.field.id, { provider: 'manual', value });
        }
    }
    edited(newValue) {
        if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
            this.clear();
        }
        else {
            const value = { latitude: newValue.latitude, longitude: newValue.longitude, altitude: newValue.altitude };
            this.state.setFieldValue(this.field.id, { provider: 'edited', value });
        }
    }
    gps(newValue, raw) {
        if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
            this.clear();
            return;
        }
        const value = {
            latitude: newValue.latitude,
            longitude: newValue.longitude,
            altitude: newValue.altitude,
        };
        if (newValue.accuracy != null) {
            value.accuracy = newValue.accuracy;
        }
        const stateValue = { provider: 'gps', value };
        if (raw != null) {
            object_1.IkeObject.assignNotNull(stateValue, extractLocationRaw(raw));
        }
        this.state.setFieldValue(this.field.id, stateValue);
    }
    capture(captureId) {
        this.state.setFieldValue(this.field.id, { provider: 'truesize', captureId });
    }
    clear() {
        this.state.setFieldValue(this.field.id, null);
    }
}
exports.ModelMutatorLocation = ModelMutatorLocation;
class CollectionModelFieldLocation extends collection_field_1.CollectionModelField {
    static isLocationField(f) {
        return f.schema.type === CollectionModelFieldLocation.TYPE;
    }
    static isValidLocation(l) {
        if (l == null) {
            return false;
        }
        if (isNaN(l.latitude) || isNaN(l.longitude)) {
            return false;
        }
        if (l.latitude > LocationRange.latitude.max || l.latitude < LocationRange.latitude.min) {
            return false;
        }
        if (l.longitude > LocationRange.longitude.max || l.longitude < LocationRange.longitude.min) {
            return false;
        }
        return l.latitude !== DEFAULT_LOCATION_DATA.latitude || l.longitude !== DEFAULT_LOCATION_DATA.longitude;
    }
    get Mutator() {
        if (this._Mutator == null) {
            this._Mutator = new ModelMutatorLocation(this);
        }
        return this._Mutator;
    }
    get value() {
        if (this.stateValue == null) {
            return {
                latitude: 0,
                longitude: 0,
                altitude: 0,
                accuracy: 0,
            };
        }
        if (this.isEdited()) {
            return this.stateValue.value;
        }
        if (this.isTrueSize()) {
            const [capture] = this.getCaptures();
            if (capture == null) {
                return null;
            }
            return capture.target;
        }
        if (this.isGPS()) {
            return this.stateValue.value;
        }
        if (this.isManual()) {
            return this.stateValue.value;
        }
        return null;
    }
    /**
     * Get the raw location data calculating alternative altitudes
     *
     * @remarks This cannot be null
     */
    get raw() {
        if (!this.isGPS()) {
            return {};
        }
        const stateValue = this.stateValue;
        const isAntennaHeightMissing = stateValue.antennaHeight == null || stateValue.antennaHeight === 0;
        if (isAntennaHeightMissing) {
            return Object.assign(Object.assign({}, extractLocationRaw(stateValue)), { deviceAltitude: stateValue.value.altitude, antennaHeight: undefined });
        }
        return Object.assign(Object.assign({}, extractLocationRaw(stateValue)), { deviceAltitude: stateValue.antennaHeight + stateValue.value.altitude, groundAltitude: stateValue.value.altitude, antennaHeight: stateValue.antennaHeight });
    }
    get provider() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.provider == null) {
            return 'manual';
        }
        return stateValue.provider;
    }
    isManual() {
        return this.provider === CollectionModelFieldLocation.PROVIDER_MANUAL;
    }
    isEdited() {
        return this.provider === CollectionModelFieldLocation.PROVIDER_EDITED;
    }
    isTrueSize() {
        return this.provider === CollectionModelFieldLocation.PROVIDER_TRUE_SIZE;
    }
    isGPS() {
        return this.provider === CollectionModelFieldLocation.PROVIDER_GPS;
    }
    getOwnedCaptures() {
        return this.getCaptures();
    }
    getCaptures() {
        return this.getCaptureIds()
            .map((c) => this.collection.state.getCapture(c))
            .filter((c) => !!c);
    }
    getOwnedCaptureIds() {
        return this.getCaptureIds();
    }
    getCaptureIds() {
        if (this.isTrueSize()) {
            return [this.stateValue.captureId];
        }
        return [];
    }
    hasValue() {
        return CollectionModelFieldLocation.isValidLocation(this.value);
    }
}
exports.CollectionModelFieldLocation = CollectionModelFieldLocation;
CollectionModelFieldLocation.TYPE = core_api_1.IkeFieldType.LOCATION;
CollectionModelFieldLocation.PROVIDER_MANUAL = 'manual';
CollectionModelFieldLocation.PROVIDER_GPS = 'gps';
CollectionModelFieldLocation.PROVIDER_TRUE_SIZE = 'truesize';
CollectionModelFieldLocation.PROVIDER_EDITED = 'edited';
//# sourceMappingURL=location.field.js.map